import { supabase } from '@/integrations/supabase/client';
import { getAllCoursesUnified } from '@/utils/allCourses';
import { logger } from '@/utils/logger';

export interface ModuleMismatch {
  moduleId: string;
  moduleTitle: string;
  existsInDatabase: boolean;
  existsInStatic: boolean;
  order?: number;
}

export interface CourseSyncReport {
  courseId: string;
  courseTitle: string;
  staticModuleCount: number;
  databaseModuleCount: number;
  isSynced: boolean;
  mismatches: ModuleMismatch[];
  syncStatus: 'synced' | 'static_missing' | 'database_missing' | 'both_missing';
}

export interface SyncResult {
  totalCourses: number;
  syncedCourses: number;
  mismatchedCourses: number;
  reports: CourseSyncReport[];
  generatedAt: string;
}

/**
 * Course Sync Service - Compare database vs static module definitions
 * and provide tools for reconciliation
 */
export class CourseSyncService {
  /**
   * Audit all courses comparing database vs static definitions
   */
  static async auditAllCourses(): Promise<SyncResult> {
    try {
      // Get all courses from database
      const { data: dbCourses, error: coursesError } = await supabase
        .from('courses')
        .select('id, title')
        .eq('is_active', true);

      if (coursesError) throw coursesError;

      // Get all modules from database grouped by course
      const { data: dbModules, error: modulesError } = await supabase
        .from('course_modules')
        .select('course_id, module_id, module_title, module_order')
        .eq('is_enabled', true)
        .order('module_order', { ascending: true });

      if (modulesError) throw modulesError;

      // Get all static courses
      const staticCourses = getAllCoursesUnified();

      // Build database module map
      const dbModuleMap = new Map<string, Array<{ id: string; title: string; order: number }>>();
      dbModules?.forEach(m => {
        if (!dbModuleMap.has(m.course_id)) {
          dbModuleMap.set(m.course_id, []);
        }
        dbModuleMap.get(m.course_id)!.push({
          id: m.module_id,
          title: m.module_title,
          order: m.module_order
        });
      });

      // Build static module map
      const staticModuleMap = new Map<string, Array<{ id: string; title: string }>>();
      staticCourses.forEach(course => {
        const modules = course.modules?.map(m => ({ id: m.id, title: m.title })) || [];
        staticModuleMap.set(course.id, modules);
      });

      // Generate reports for all courses
      const reports: CourseSyncReport[] = [];
      const allCourseIds = new Set([
        ...(dbCourses?.map(c => c.id) || []),
        ...staticCourses.map(c => c.id)
      ]);

      for (const courseId of allCourseIds) {
        const dbCourse = dbCourses?.find(c => c.id === courseId);
        const staticCourse = staticCourses.find(c => c.id === courseId);
        
        const dbMods = dbModuleMap.get(courseId) || [];
        const staticMods = staticModuleMap.get(courseId) || [];

        const mismatches: ModuleMismatch[] = [];

        // Find modules in database but not in static
        dbMods.forEach(dbMod => {
          const existsInStatic = staticMods.some(sm => sm.id === dbMod.id);
          if (!existsInStatic) {
            mismatches.push({
              moduleId: dbMod.id,
              moduleTitle: dbMod.title,
              existsInDatabase: true,
              existsInStatic: false,
              order: dbMod.order
            });
          }
        });

        // Find modules in static but not in database
        staticMods.forEach(staticMod => {
          const existsInDb = dbMods.some(dm => dm.id === staticMod.id);
          if (!existsInDb) {
            mismatches.push({
              moduleId: staticMod.id,
              moduleTitle: staticMod.title,
              existsInDatabase: false,
              existsInStatic: true
            });
          }
        });

        const isSynced = mismatches.length === 0;
        let syncStatus: CourseSyncReport['syncStatus'] = 'synced';
        
        if (!isSynced) {
          const hasDbOnly = mismatches.some(m => m.existsInDatabase && !m.existsInStatic);
          const hasStaticOnly = mismatches.some(m => !m.existsInDatabase && m.existsInStatic);
          
          if (hasDbOnly && hasStaticOnly) {
            syncStatus = 'both_missing';
          } else if (hasDbOnly) {
            syncStatus = 'static_missing';
          } else {
            syncStatus = 'database_missing';
          }
        }

        reports.push({
          courseId,
          courseTitle: dbCourse?.title || staticCourse?.title || courseId,
          staticModuleCount: staticMods.length,
          databaseModuleCount: dbMods.length,
          isSynced,
          mismatches,
          syncStatus
        });
      }

      // Sort by mismatch count (most mismatches first)
      reports.sort((a, b) => b.mismatches.length - a.mismatches.length);

      const syncedCourses = reports.filter(r => r.isSynced).length;

      return {
        totalCourses: reports.length,
        syncedCourses,
        mismatchedCourses: reports.length - syncedCourses,
        reports,
        generatedAt: new Date().toISOString()
      };
    } catch (error) {
      logger.error('Failed to audit courses:', error);
      throw error;
    }
  }

  /**
   * Get sync report for a specific course
   */
  static async getCourseDiscrepancies(courseId: string): Promise<CourseSyncReport | null> {
    const result = await this.auditAllCourses();
    return result.reports.find(r => r.courseId === courseId) || null;
  }

  /**
   * Get all courses with mismatches (for admin dashboard)
   */
  static async getMismatchedCourses(): Promise<CourseSyncReport[]> {
    const result = await this.auditAllCourses();
    return result.reports.filter(r => !r.isSynced);
  }

  /**
   * Get database modules for a course (for runtime fallback)
   */
  static async getDatabaseModules(courseId: string): Promise<Array<{
    id: string;
    title: string;
    description: string;
    order: number;
    content?: string;
  }>> {
    try {
      const { data: modules, error } = await supabase
        .from('course_modules')
        .select(`
          module_id,
          module_title,
          module_order,
          module_config
        `)
        .eq('course_id', courseId)
        .eq('is_enabled', true)
        .order('module_order', { ascending: true });

      if (error) throw error;

      return (modules || []).map(m => ({
        id: m.module_id,
        title: m.module_title,
        description: (m.module_config as any)?.description || '',
        order: m.module_order,
        content: (m.module_config as any)?.content
      }));
    } catch (error) {
      logger.error(`Failed to get database modules for ${courseId}:`, error);
      return [];
    }
  }

  /**
   * Get module content from module_content table
   */
  static async getModuleContent(courseId: string, moduleId: string): Promise<{
    content: string;
    difficulty_level: string;
    estimated_time: number | null;
  } | null> {
    try {
      const { data, error } = await supabase
        .from('module_content')
        .select('content, difficulty_level, estimated_time')
        .eq('course_id', courseId)
        .eq('module_id', moduleId)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.warn(`No module content found for ${courseId}/${moduleId}:`, error);
      return null;
    }
  }

  /**
   * Generate placeholder module from database definition
   * Used when static module is missing
   */
  static generatePlaceholderModule(dbModule: {
    id: string;
    title: string;
    description?: string;
    order?: number;
    content?: string;
  }): any {
    return {
      id: dbModule.id,
      title: dbModule.title,
      description: dbModule.description || `Module: ${dbModule.title}`,
      content: dbModule.content || `# ${dbModule.title}\n\nContent is being prepared. Please check back soon.`,
      duration: '30 minutes',
      difficulty: 'intermediate',
      objectives: [`Complete ${dbModule.title}`],
      order: dbModule.order
    };
  }

  /**
   * Get summary stats for sync status
   */
  static async getSyncSummary(): Promise<{
    totalCourses: number;
    syncedCourses: number;
    coursesWithMissingStatic: number;
    coursesWithMissingDb: number;
    totalMissingModules: number;
  }> {
    const result = await this.auditAllCourses();
    
    let coursesWithMissingStatic = 0;
    let coursesWithMissingDb = 0;
    let totalMissingModules = 0;

    result.reports.forEach(r => {
      if (r.syncStatus === 'static_missing' || r.syncStatus === 'both_missing') {
        coursesWithMissingStatic++;
      }
      if (r.syncStatus === 'database_missing' || r.syncStatus === 'both_missing') {
        coursesWithMissingDb++;
      }
      totalMissingModules += r.mismatches.length;
    });

    return {
      totalCourses: result.totalCourses,
      syncedCourses: result.syncedCourses,
      coursesWithMissingStatic,
      coursesWithMissingDb,
      totalMissingModules
    };
  }
}
